import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { DoorOpen, KeyRound, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errCode, formatXAF } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const go = (navigate, data) => navigate(data.status === "matched" ? `/game/${data.game_id}` : "/queue");

const JoinDialog = ({ open, onOpenChange, initialCode }) => {
  const { t, tErr, lang } = useI18n();
  const navigate = useNavigate();
  const [code, setCode] = useState(initialCode || "");
  const [room, setRoom] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setRoom(null);
    if (code.trim().length !== 6) return;
    api.get(`/rooms/${code.trim()}`).then((r) => setRoom(r.data)).catch(() => setRoom(false));
  }, [code]);
  const join = async () => {
    setBusy(true);
    try {
      go(navigate, (await api.post(`/rooms/${code.trim()}/join`)).data);
    } catch (e) {
      toast.error(tErr(errCode(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-white/10 bg-[#121620] sm:max-w-sm" data-testid="join-room-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("room.joinTitle")}</DialogTitle></DialogHeader>
        <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={6} placeholder="ABC123"
          className="h-14 rounded-xl border-white/10 bg-white/5 text-center font-mono text-2xl tracking-[0.4em]" data-testid="join-room-code-input" />
        {room && (
          <p className="text-center text-sm text-slate-300" data-testid="join-room-info">
            {t("room.info", { name: room.creator_pseudo, n: room.player_count, stake: formatXAF(room.stake, lang), w: room.waiting })}
            {room.status !== "open" && <span className="block text-red-300">{t("room.closed")}</span>}
          </p>
        )}
        {room === false && <p className="text-center text-sm text-red-300">{t("errors.room_not_found")}</p>}
        <button onClick={join} disabled={busy || !room || room.status !== "open"} data-testid="join-room-submit-btn"
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-amber-500 font-display font-bold text-[#0B0D12] disabled:opacity-50">
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} {t("room.join", { stake: room ? formatXAF(room.stake, lang) : "" })}
        </button>
      </DialogContent>
    </Dialog>
  );
};

export const RoomButtons = ({ n, stake, valid }) => {
  const { t, tErr } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [joinOpen, setJoinOpen] = useState(!!params.get("code"));
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      go(navigate, (await api.post("/rooms", { player_count: n, stake })).data);
    } catch (e) {
      toast.error(tErr(errCode(e)));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={create} disabled={busy || !valid} className="flex h-12 items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-500/50 disabled:opacity-50" data-testid="lobby-private-room-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <DoorOpen className="h-4 w-4" />} {t("lobby.privateRoom")}
        </button>
        <button onClick={() => setJoinOpen(true)} className="flex h-12 items-center justify-center gap-2 rounded-full border border-white/15 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-500/50" data-testid="lobby-join-code-btn">
          <KeyRound className="h-4 w-4" /> {t("lobby.joinCode")}
        </button>
      </div>
      <JoinDialog open={joinOpen} onOpenChange={setJoinOpen} initialCode={params.get("code") || ""} />
    </>
  );
};
